import React, { useState, useRef, useEffect, useContext } from 'react';
import { updateFamilyMemberDetails } from '../../api';
import { ChildContext } from '../../context/ChildContext';
import { UserContext } from '../../context/UserContext';
import MyAvatarSelection from '../basic/MyAvatarSelection';
import placeholderAvatar from '../../assets/Avatars/placeholder/placeholder.png';

import './AvatarDisplay.css';

const avatarContext = require.context('../../assets/Avatars', false, /\.(png|jpe?g|svg)$/);
const avatars = Object.fromEntries(
  avatarContext.keys().map(key => [key.replace('./', ''), avatarContext(key)])
);

function AvatarDisplay({ imageUrl, childName }) {
    const [isHovered, setIsHovered] = useState(false);
    const [showSelection, setShowSelection] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const avatarRef = useRef(null);
    const { refetchChildData } = useContext(ChildContext);
    const { user, setUser } = useContext(UserContext);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (avatarRef.current && !avatarRef.current.contains(event.target)) {
                setShowSelection(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleAvatarClick = (e) => {
        e.stopPropagation();
        setShowSelection(prev => !prev);
    };

    const handleSelectAvatar = async (newAvatarUrl) => {
        setIsUpdating(true);
        try {
            await updateFamilyMemberDetails(user.details.user_id, { avatar_url: newAvatarUrl });
            setShowSelection(false);
            
            // Update local user state
            setUser(prevUser => ({
                ...prevUser,
                details: { ...prevUser.details, avatar_url: newAvatarUrl }
            }));
            
            // Refetch child data to ensure everything is up to date
            await refetchChildData();
        } catch (error) {
            console.error('Failed to update avatar:', error);
            // Optionally, add user-facing error message here
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="avatar-display" ref={avatarRef}>
            <div className="avatar-display__frame">
                <div 
                    className="avatar-display__image" 
                    style={{
                        backgroundImage: `url(${imageUrl ? (avatars[imageUrl] || placeholderAvatar) : placeholderAvatar})`,
                        opacity: isUpdating ? 0.5 : 1
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={handleAvatarClick}
                >
                    {isHovered && !isUpdating && (
                        <div className="avatar-display__edit-overlay">
                            <span className="avatar-display__edit-icon"></span>
                        </div>
                    )}
                    {isUpdating && <div className="avatar-display__loading-overlay">Updating...</div>}
                </div>
                <h3 className="avatar-display__name">{childName}</h3>
            </div>
            {showSelection && !isUpdating && (
                <div className="avatar-display__selection-tooltip">
                    <MyAvatarSelection
                        currentAvatar={imageUrl || 'placeholder.png'}
                        onSelectAvatar={handleSelectAvatar}
                    />
                </div>
            )}
        </div>
    );
}

export default AvatarDisplay;